import { cache } from "react";

export const LTM_API_BASE = "https://5swsvgnwlb.execute-api.ap-south-1.amazonaws.com/Prod";

export type VoiceNote = {
  voiceNoteId: string;
  audioUrl: string;
  duration: number;
  durationSec?: number;
  waveformPeaks?: number[];
  upvotes: number;
  transcript: string | null;
};

export type Contributor = {
  userId: string;
  username: string;
  level: "explorer" | "scout" | "insider" | "legend";
  reputation: number;
  isVerified: boolean;
};

export type Place = {
  placeId: string;
  name: string;
  aliases?: string[];
  description: string;
  category: string;
  latitude: number;
  longitude: number;
  address: string;
  city: string;
  // Location hierarchy (additive — city stays for backward compat)
  state?: string;
  district?: string;
  region?: string;
  rating: number;
  reviewCount: number;
  upvoteCount?: number;
  visitedCount?: number;
  isTrending?: boolean;
  trendingScore?: number;
  heroPhotoUrl: string | null;
  voiceUrl?: string | null;
  voiceDuration?: number | null;
  videoUrl?: string | null;
  photos?: string[];
  bestTime?: string | null;
  localTip?: string | null;
  avoid?: string | null;
  isVerifiedByAdmin?: boolean;
  isHidden?: boolean;
  status?: string;
  visitDate?: string;
  createdAt: string;
  updatedAt?: string;
  voiceNotes?: VoiceNote[];
  contributor?: Contributor;
  // Classification & visit info
  tags?: string[];
  difficulty?: "easy" | "moderate" | "hard" | "extreme";
  crowdLevel?: "very-low" | "low" | "medium" | "high" | "tourist-hotspot";
  season?: "all" | "summer" | "monsoon" | "winter";
  bestFor?: string[];
  travelTime?: string;
  entryFee?: string;
  editorScore?: number;
};

export type PlaceCategory = {
  categoryId: string;
  label: string;
  icon: string;
  colorHex: string;
  isActive: boolean;
  order: number;
};

// Wrapped in React's cache() — every dynamic route under /places, /tag,
// /district, /region and the catch-all [slug] calls this from
// generateStaticParams, generateMetadata, and the page component itself,
// so without dedup that's up to 3 identical fetches of the whole places
// list per page. Mirrors the same pattern already used for getPostBySlug
// in blog.ts.
export const getAllPlaces = cache(async (): Promise<Place[]> => {
  try {
    const res = await fetch(`${LTM_API_BASE}/public/places?limit=9999`, {
      signal: AbortSignal.timeout(8000),
      next: { tags: ["places"] },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.success && Array.isArray(data.data) ? data.data : [];
  } catch {
    return [];
  }
});

export const getPlaceById = cache(async (placeId: string): Promise<Place | null> => {
  try {
    const res = await fetch(`${LTM_API_BASE}/public/places/${placeId}`, {
      signal: AbortSignal.timeout(8000),
      next: { tags: ["places", `place-${placeId}`] },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.success ? data.data : null;
  } catch {
    return null;
  }
});

export const getPlaceCategories = cache(async (): Promise<PlaceCategory[]> => {
  try {
    const res = await fetch(`${LTM_API_BASE}/categories`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.success ? data.data.categories : [];
  } catch {
    return [];
  }
});

export function slugifyText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function placeSlug(p: { placeId: string; name: string }): string {
  return `${slugifyText(p.name)}-${p.placeId.slice(0, 8)}`;
}

export function parsePlaceIdPrefixFromSlug(slug: string): string | null {
  const match = slug.match(/-([a-f0-9]{8})$/i);
  return match ? match[1] : null;
}

// Clean, ID-free slug map ported from PHP's nohaya_build_place_slug_map()
// (php/places/lib/places_client.php) -- this is the scheme the currently-
// live, already-indexed /places/{slug} URLs use, distinct from (and newer
// than) placeSlug()/parsePlaceIdPrefixFromSlug() above, which still reflect
// the pre-PHP-migration `name-{id8}` format. Sort order matters for the
// dedup to match PHP exactly: by createdAt ascending, placeId as tiebreak
// (not insertion/array order), since that's what determines which of two
// same-named places gets the bare slug vs. the "-2" suffix.
export function buildPlaceSlugMap(places: { placeId: string; name: string; createdAt?: string }[]): Map<string, string> {
  const sorted = [...places].sort((a, b) => {
    const byDate = (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
    return byDate !== 0 ? byDate : a.placeId.localeCompare(b.placeId);
  });
  const map = new Map<string, string>();
  const seen = new Set<string>();
  for (const p of sorted) {
    const base = slugifyText(p.name);
    let slug = base;
    let n = 2;
    while (seen.has(slug)) {
      slug = `${base}-${n}`;
      n += 1;
    }
    seen.add(slug);
    map.set(p.placeId, slug);
  }
  return map;
}

export type PlaceSlugResolution =
  | { kind: "clean"; placeId: string }
  | { kind: "legacy"; placeId: string; canonicalSlug: string }
  | { kind: "not-found" };

// Single entry point for resolving an incoming /places/{slug} URL against
// both the current clean-slug scheme and the pre-migration `name-{id8}`
// format, so old indexed/bookmarked links 301 to the canonical slug instead
// of 404ing -- matches place.php's existing redirect behavior.
export function resolvePlaceSlug(
  slug: string,
  places: { placeId: string; name: string; createdAt?: string }[]
): PlaceSlugResolution {
  const slugMap = buildPlaceSlugMap(places);
  for (const [placeId, s] of slugMap) {
    if (s === slug) return { kind: "clean", placeId };
  }

  const idPrefix = parsePlaceIdPrefixFromSlug(slug);
  if (idPrefix) {
    const match = places.find((p) => p.placeId.slice(0, 8).toLowerCase() === idPrefix.toLowerCase());
    if (match) {
      const canonicalSlug = slugMap.get(match.placeId);
      if (canonicalSlug) return { kind: "legacy", placeId: match.placeId, canonicalSlug };
    }
  }

  return { kind: "not-found" };
}

export function citySlug(city: string): string {
  return slugifyText(city);
}

export function districtSlug(district: string): string {
  return slugifyText(district);
}

export function regionSlug(region: string): string {
  return slugifyText(region);
}

export function tagSlug(tag: string): string {
  return slugifyText(tag);
}

export function tagLabel(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Returns the effective district for a place: explicit district field, else city.
export function placeDistrict(p: Place): string {
  return p.district || p.city;
}

export function rankScore(p: Place): number {
  const editor = (p.editorScore ?? 50) * 0.4;
  const rating = (p.rating || 0) * 10 * 0.15;
  const reviews = Math.min((p.reviewCount || 0), 100) * 0.1;
  const visits = Math.min((p.visitedCount || 0) / 10, 10) * 0.05;
  const trending = (p.isTrending ? 20 : 0) * 0.1;
  return Math.min(100, Math.round(editor + rating + reviews + visits + trending));
}

export function getUniqueDistricts(places: Place[]): string[] {
  return Array.from(new Set(places.map(placeDistrict))).sort();
}

export function getUniqueRegions(places: Place[]): string[] {
  return Array.from(new Set(places.filter((p) => p.region).map((p) => p.region!))).sort();
}

export function getUniqueTags(places: Place[]): string[] {
  const set = new Set<string>();
  for (const p of places) {
    if (p.tags) for (const t of p.tags) set.add(t);
  }
  return Array.from(set).sort();
}

export const PLACE_TAGS = [
  "waterfall", "beach", "temple", "lake", "dam", "hill", "fort", "forest",
  "cave", "view-point", "camping", "trekking", "sunrise", "sunset", "village",
  "historical", "wildlife", "photography", "hidden-gem", "family-friendly",
  "kids", "adventure", "weekend-trip",
] as const;

export const PLACE_BEST_FOR = [
  "solo", "couples", "family", "friends", "kids", "photographers",
  "adventure-lovers", "nature-lovers",
] as const;

// `place.category` is inconsistent at the data source: older entries store the
// human-readable label directly, newer ones (added via the app) store the raw
// categoryId. Resolve either form to the display label.
export function resolveCategoryLabel(category: string | undefined | null, categories: PlaceCategory[]): string {
  if (!category) return "";
  const match = categories.find((c) => c.categoryId === category);
  return match ? match.label : category;
}
